import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Code2, Cpu, BookOpen, Coffee } from 'lucide-react';
import Navbar from '../components/Navbar';

const skills = ['C++', 'C', 'Theoretical Physics', 'Visual Basic', 'Memory Management', 'Strategic Planning', 'Robotics', 'Critical Thinking', 'Team Leadership', 'Public Speaking'];

const interests = [
  { icon: Code2, label: 'Web Development', desc: 'Building modern, responsive apps with clean architecture' },
  { icon: Cpu, label: 'Tech & AI', desc: 'Exploring AI tools, automation, and emerging technologies' },
  { icon: BookOpen, label: 'Continuous Learning', desc: 'Always picking up new skills and frameworks' },
  { icon: Coffee, label: 'Problem Solving', desc: 'Breaking down complex challenges into elegant solutions' },
];

function FaceParticle({ id, x, onDone }) {
  return (
    <motion.img
      src="/mbk-neon.png?v=2"
      className="fixed top-0 z-[999] pointer-events-none rounded-full"
      style={{ left: `${x}%`, width: 32, height: 32, objectFit: 'cover' }}
      initial={{ y: -40, opacity: 1, rotate: 0, scale: 0.8 }}
      animate={{ y: '105vh', opacity: [1, 1, 0], rotate: Math.random() > 0.5 ? 180 : -180, scale: [0.8, 1.1, 0.7] }}
      transition={{ duration: 1.8 + Math.random() * 1, ease: 'easeIn' }}
      onAnimationComplete={onDone}
    />
  );
}

export default function About() {
  const [particles, setParticles] = useState([]);
  const [cvPos, setCvPos] = useState({ x: 0, y: 0 });
  const cvRef = useRef(null);

  // Proximity-escape: anywhere the cursor moves on the page, if it gets close to
  // the CV button, the button leaps away. Combined with pointer-events: none on the
  // button itself, this guarantees it can never be clicked by mouse.
  useEffect(() => {
    const SAFE_RADIUS = 110;   // start running when cursor is this close (px)
    const JUMP = 70;           // gentle hop each escape — stays on screen, easy to spot
    const MARGIN = 40;         // keep the button on-screen
    let lastMove = 0;          // throttle so it glides instead of teleporting

    const onMove = (e) => {
      const now = performance.now();
      if (now - lastMove < 60) return;  // ~16fps escape ticks → smooth, not frantic
      const el = cvRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = cx - e.clientX;
      const dy = cy - e.clientY;
      const dist = Math.hypot(dx, dy);
      if (dist > SAFE_RADIUS) return;
      lastMove = now;

      // pick an angle away from the cursor (with a little chaos)
      const baseAngle = Math.atan2(dy, dx);
      const jitter = (Math.random() - 0.5) * 0.6;
      const angle = baseAngle + jitter;

      setCvPos(prev => {
        let nx = prev.x + Math.cos(angle) * JUMP;
        let ny = prev.y + Math.sin(angle) * JUMP;
        // clamp to viewport
        const maxX = window.innerWidth - rect.right - MARGIN + prev.x;
        const minX = -(rect.left - MARGIN) + prev.x;
        const maxY = window.innerHeight - rect.bottom - MARGIN + prev.y;
        const minY = -(rect.top - MARGIN) + prev.y;
        nx = Math.max(minX, Math.min(maxX, nx));
        ny = Math.max(minY, Math.min(maxY, ny));
        return { x: nx, y: ny };
      });
    };

    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleCvMouseEnter = useCallback(() => {
    // safety net — should rarely fire because the global handler escapes earlier
  }, []);

  const sprinkle = () => {
    const newParticles = Array.from({ length: 18 }, (_, i) => ({
      id: Date.now() + i,
      x: 5 + Math.random() * 90,
    }));
    setParticles(prev => [...prev, ...newParticles]);
  };

  const removeParticle = (id) => setParticles(prev => prev.filter(p => p.id !== id));

  return (
    <div className="min-h-screen bg-background dot-bg">
      <AnimatePresence>
        {particles.map(p => (
          <FaceParticle key={p.id} id={p.id} x={p.x} onDone={() => removeParticle(p.id)} />
        ))}
      </AnimatePresence>
      <Navbar />

      <div className="max-w-4xl mx-auto px-8 pt-32 pb-20">

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
            <div>
              <span className="text-xs font-semibold text-primary bg-primary/10 border border-primary/30 px-3 py-1 rounded-full font-mono-cv tracking-widest uppercase">About Me</span>
              <h2 className="text-4xl font-extrabold text-foreground mt-4" style={{ fontFamily: 'Syne, sans-serif' }}>The story so far.</h2>
            </div>
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-2xl animate-pulse" />
              <img
                src="/mbk-neon.png?v=2"
                alt="mbk outline"
                className="relative w-64 h-64 object-cover rounded-full border-2 border-primary/40 shadow-lg shadow-primary/20"
              />
            </div>
          </div>
          <div className="border-l-2 border-primary/30 pl-5 mb-12">
            <p className="text-foreground/70 text-sm leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
              me versus me so I don't really care. If people doubt · popular loner kid,<br />
              but still on go if shit gets wild...
            </p>
            <p className="text-primary/40 text-xs font-mono-cv mt-2">~ mbk, always</p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <h3 className="text-primary font-bold text-lg mb-4 font-mono-cv">// Bio</h3>
            <div className="space-y-3 text-muted-foreground leading-relaxed text-sm">
              <p>hey, i'm bilal… or mbk, or bil <span className="text-primary/70 font-mono-cv text-xs">(long story, blame my 5 y/o sister)</span></p>
              <p>i code, i build, i talk, i rant, i listen</p>
              <p>i like people, i like conversations, and those random talks that go on way too long</p>
              <p>was an only child for years so yeah… slightly spoiled <span className="text-primary/70 font-mono-cv text-xs">(my mom would say more than slightly)</span></p>

              <p>not a netflix and chill person… but more of a walk and chill type</p>
              <p>give me spotify, a long road, and my thoughts — and i'm good</p>
              <p className="text-primary/80 font-mono-cv text-xs italic pt-1">"life is a chammak challo" ~ mbk</p>
            </div>
            <button onClick={sprinkle} className="mt-6 inline-flex items-center gap-2 border border-primary/50 text-primary text-sm font-semibold px-5 py-2 rounded-lg hover:bg-primary/10 transition-all active:scale-95">
              idk
            </button>
            <div
              ref={cvRef}
              onMouseEnter={handleCvMouseEnter}
              style={{ transform: `translate(${cvPos.x}px, ${cvPos.y}px)`, transition: 'transform 0.25s cubic-bezier(0.25,0.46,0.45,0.94)', display: 'inline-block' }}
              className="mt-2"
            >
              <button
                tabIndex={-1}
                aria-hidden="true"
                onClick={(e) => e.preventDefault()}
                style={{ pointerEvents: 'none' }}
                className="inline-flex items-center gap-2 border border-primary/50 text-primary text-sm font-semibold px-5 py-2 rounded-lg select-none"
              >
                Download CV
              </button>
            </div>

          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h3 className="text-primary font-bold text-lg mb-4 font-mono-cv">// Skills</h3>
            <div className="flex flex-wrap gap-2 mb-8">
              {skills.map((skill, i) => (
                <motion.span
                  key={skill}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + i * 0.05 }}
                  className="font-mono-cv text-xs px-3 py-1.5 bg-secondary border border-border text-primary rounded-md hover:border-primary/60 transition-colors cursor-default"
                >
                  {skill}
                </motion.span>
              ))}
            </div>

            <h3 className="text-primary font-bold text-lg mb-4 font-mono-cv">// Education</h3>
            <div className="bg-card border border-border rounded-xl p-4 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-bold text-foreground text-sm">B.Sc. — LUMS</p>
                  <p className="text-muted-foreground text-xs mt-1 font-mono-cv">Currently Enrolled · 2025 – Present</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary/50 mt-2 flex-shrink-0" />
                <div>
                  <p className="font-bold text-foreground text-sm">A-Levels — LGS JT</p>
                  <p className="text-muted-foreground text-xs mt-0.5 font-mono-cv">2023 – 2025</p>
                  <p className="text-primary/70 text-xs font-mono-cv">Stream: Sciences (Grades: A*'s)</p>
                  </div>
                  </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-emerald-400/70 mt-2 flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-bold text-foreground text-sm">SAT</p>
                    <span className="font-mono-cv text-[10px] px-1.5 py-0.5 rounded-full border text-emerald-400/80 bg-emerald-400/10 border-emerald-400/20">1st attempt</span>
                  </div>
                  <p className="text-primary/70 text-xs font-mono-cv">Score: 1510 (740 R/W · 770 Maths)</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
          <h3 className="text-primary font-bold text-lg mb-6 font-mono-cv">// Courses Completed</h3>
          <div className="space-y-6">
            <div>
              <p className="text-xs text-muted-foreground font-mono-cv tracking-widest mb-3">FALL 2025 — SEMESTER I</p>
              <div className="flex flex-wrap gap-2">
                {['SS 101', 'MATH 101', 'CS 100', 'PHY 101', 'CHEM 101', 'SSE 101', 'EE 100'].map((c, i) => (
                  <motion.span key={c} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 + i * 0.05 }}
                    className="font-mono-cv text-xs px-3 py-1.5 bg-secondary border border-border text-primary rounded-md hover:border-primary/60 transition-colors cursor-default">
                    {c}
                  </motion.span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-mono-cv tracking-widest mb-3">SPRING 2026 — SEMESTER II</p>
              <div className="flex flex-wrap gap-2">
                {['MATH 120', 'PHY 104', 'BIO 100', 'BIO 101', 'CS 200', 'CHEM 100', 'SSE 101'].map((c, i) => (
                  <motion.span key={c} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6 + i * 0.05 }}
                    className="font-mono-cv text-xs px-3 py-1.5 bg-secondary border border-border text-primary rounded-md hover:border-primary/60 transition-colors cursor-default">
                    {c}
                  </motion.span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}